/**
 * Product card quick shop:
 * - lazy image slider (arrows, images created on demand)
 * - linked colour switching (image, price, sizes, links)
 * - hover size row add-to-cart (delegates to the theme's <product-form>)
 */
(() => {
  const dataCache = new WeakMap();

  function getEntries(card) {
    if (dataCache.has(card)) return dataCache.get(card);

    let entries = [];
    const script = card.querySelector('script[data-quick-shop-data]');
    if (script) {
      try {
        entries = JSON.parse(script.textContent);
      } catch (error) {
        entries = [];
      }
    }

    dataCache.set(card, entries);
    return entries;
  }

  function getState(card) {
    return {
      colour: parseInt(card.dataset.qsColour || '0', 10),
      slide: parseInt(card.dataset.qsSlide || '0', 10),
    };
  }

  function mediaItem(entry, index) {
    const item = entry && Array.isArray(entry.media) ? entry.media[index] : null;
    if (!item) return null;
    if (typeof item === 'string') return { type: 'image', image: item };
    return item;
  }

  function mediaImage(item) {
    if (!item) return '';
    if (typeof item === 'string') return item;
    return item.image || '';
  }

  function isVideoItem(item) {
    return !!(item && typeof item === 'object' && item.src && item.type === 'video');
  }

  function getCardVideo(card) {
    let video = card.querySelector('video.product-card__slide-video');
    if (video) return video;

    const media = card.querySelector('.product-card__media');
    if (!media) return null;

    video = document.createElement('video');
    video.className = 'product-card__slide-video';
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
    video.preload = 'auto';
    video.setAttribute('aria-hidden', 'true');
    video.hidden = true;
    media.appendChild(video);
    return video;
  }

  function hideCardVideo(card) {
    const video = card.querySelector('video.product-card__slide-video');
    if (!video) return;
    video.pause();
    video.removeAttribute('src');
    video.load();
    video.hidden = true;
    card.classList.remove('is-showing-video');
  }

  function playCardVideo(card, item) {
    const video = getCardVideo(card);
    if (!video || !item || !item.src) return;

    video.hidden = false;
    if (item.image) video.poster = item.image;
    if (video.getAttribute('src') !== item.src) {
      video.src = item.src;
      video.load();
    }
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    const playPromise = video.play();
    if (playPromise && playPromise.catch) playPromise.catch(() => {});
    card.classList.add('is-showing-video');
  }

  function showSlide(card, item, onReady) {
    if (isVideoItem(item)) {
      playCardVideo(card, item);
      if (onReady) onReady();
      return;
    }

    hideCardVideo(card);
    setImage(card, mediaImage(item), onReady);
  }

  function applyPrimarySrc(img, url) {
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.src = url;
    img.classList.remove('is-loading');
  }

  function setImage(card, url, onReady) {
    const img = card.querySelector('.product-card__image--primary');
    if (!img || !url) {
      if (onReady) onReady();
      return;
    }

    const finish = () => {
      applyPrimarySrc(img, url);
      if (onReady) onReady();
    };

    if (img.getAttribute('src') === url || img.currentSrc === url) {
      finish();
      return;
    }

    const preload = new Image();
    preload.onload = finish;
    preload.onerror = () => {
      img.classList.remove('is-loading');
      if (onReady) onReady();
    };
    preload.src = url;
    if (preload.complete && preload.naturalWidth > 0) finish();
  }

  function setSecondaryImage(card, url) {
    const img = card.querySelector('.product-card__image--secondary');
    if (!img || !url) return;

    const preload = new Image();
    preload.onload = () => {
      img.removeAttribute('srcset');
      img.removeAttribute('sizes');
      img.src = url;
    };
    preload.src = url;
  }

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#39;',
    })[char]);
  }

  function isFinePointer() {
    return window.matchMedia('(pointer: fine)').matches;
  }

  function updateProgress(card) {
    const bar = card.querySelector('[data-card-progress]');
    if (!bar) return;

    const entries = getEntries(card);
    const { colour, slide } = getState(card);
    const entry = entries[colour];
    const count = entry && Array.isArray(entry.media) ? entry.media.length : 0;

    if (count < 2) {
      bar.hidden = true;
      return;
    }

    bar.hidden = false;
    bar.style.setProperty('--card-slide-count', String(count));
    bar.style.setProperty('--card-slide-index', String(slide));
  }

  function onNav(card, direction) {
    const entries = getEntries(card);
    const { colour, slide } = getState(card);
    const entry = entries[colour];
    if (!entry || !Array.isArray(entry.media) || entry.media.length < 2) return;

    // Desktop hover already shows media[1]. Start from there so next is not a flash.
    let current = slide;
    if (isFinePointer() && !card.classList.contains('is-sliding') && slide === 0) {
      current = 1;
    }

    const next = (current + direction + entry.media.length) % entry.media.length;
    card.dataset.qsSlide = String(next);
    showSlide(card, mediaItem(entry, next), () => {
      card.classList.add('is-sliding');
    });
    updateProgress(card);
  }

  function endSliding(card) {
    if (!card.classList.contains('is-sliding')) return;
    card.classList.remove('is-sliding');
    card.dataset.qsSlide = '0';

    const entries = getEntries(card);
    const { colour } = getState(card);
    const entry = entries[colour];
    if (!entry) return;
    hideCardVideo(card);
    setImage(card, entry.image || mediaImage(mediaItem(entry, 0)));
    if (entry.media && entry.media[1]) setSecondaryImage(card, mediaImage(mediaItem(entry, 1)));
    updateProgress(card);
  }

  function rebuildSizes(card, entry) {
    const row = card.querySelector('[data-size-row]');
    if (!row || !Array.isArray(entry.sizes)) return;

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    row.innerHTML = entry.sizes
      .map((size) => {
        const disabled = size.available ? '' : ' is-disabled';
        const disabledAttr = size.available ? '' : ' disabled';
        return (
          '<product-form>' +
          `<form method="post" action="${root}cart/add">` +
          '<input type="hidden" name="quantity" value="1">' +
          `<input type="hidden" name="id" value="${size.id}">` +
          `<button type="submit" class="product-card__size-btn${disabled}"${disabledAttr}>${escapeHtml(size.value)}</button>` +
          '</form>' +
          '</product-form>'
        );
      })
      .join('');
  }

  function updatePrice(card, entry) {
    const salePrice = card.querySelector('.price-list sale-price');
    if (salePrice && entry.price) {
      const srOnly = salePrice.querySelector('.sr-only');
      salePrice.textContent = '';
      if (srOnly) salePrice.appendChild(srOnly);
      salePrice.append(entry.price);
    }

    const compareAt = card.querySelector('.price-list compare-at-price');
    if (compareAt) {
      compareAt.hidden = !entry.compareAt;
      if (entry.compareAt) {
        const srOnly = compareAt.querySelector('.sr-only');
        compareAt.textContent = '';
        if (srOnly) compareAt.appendChild(srOnly);
        compareAt.append(entry.compareAt);
      }
    }
  }

  function onSwatch(card, button) {
    const index = parseInt(button.dataset.colourIndex, 10);
    const entries = getEntries(card);
    const entry = entries[index];
    if (!entry) return;

    card.dataset.qsColour = String(index);
    card.dataset.qsSlide = '0';

    card.querySelectorAll('.product-card__colour-swatch').forEach((swatch) => {
      swatch.classList.toggle('is-active', swatch === button);
    });

    hideCardVideo(card);
    setImage(card, entry.image);
    // Keep hover second image on the new colourway
    if (Array.isArray(entry.media) && entry.media.length > 1) {
      setSecondaryImage(card, mediaImage(mediaItem(entry, 1)));
    }

    card.querySelectorAll('a.product-card__media, a.product-title').forEach((link) => {
      link.href = entry.url;
    });

    const hasSlider = Array.isArray(entry.media) && entry.media.length > 1;
    card.querySelectorAll('[data-card-nav]').forEach((nav) => {
      nav.hidden = !hasSlider;
    });
    updateProgress(card);

    updatePrice(card, entry);
    rebuildSizes(card, entry);

    const modal = card.querySelector('quick-buy-modal');
    if (modal) modal.setAttribute('product-url', entry.url);

    const wishlist = card.querySelector('[data-wishlist-btn]');
    if (wishlist) {
      wishlist.dataset.productUrl = entry.url;
      wishlist.dataset.productImage = entry.image;
    }
  }

  /**
   * Lazy sibling loading: cards render with only their own colour dot plus
   * placeholders (server-side sibling lookups were causing render timeouts /
   * 500 errors on collections). When a card nears the viewport we fetch its
   * full colourway list from the "card-siblings" section and build the real
   * dots, after which the normal swatch click handling takes over.
   */
  function swatchButtonHtml(entry, index, activeIndex) {
    const colourKey = String(entry.colour || '').toLowerCase();
    const hexKey = String(entry.hex || '').toLowerCase();
    const light =
      colourKey === 'white' ||
      colourKey === 'ivory' ||
      hexKey === '#fff' ||
      hexKey === '#ffffff' ||
      hexKey === '#fffff0';

    return (
      '<button type="button" class="product-card__colour-swatch' +
      (index === activeIndex ? ' is-active' : '') +
      (light ? ' product-card__colour-swatch--light' : '') +
      `" data-colour-index="${index}" title="${escapeHtml(entry.colour)}" aria-label="${escapeHtml(entry.colour)}"` +
      ` style="--swatch-color: ${entry.hex || '#d8d8d8'};"></button>`
    );
  }

  async function loadSiblings(card) {
    if (card.dataset.qsSiblings) return;
    card.dataset.qsSiblings = 'loading';

    const handle = card.getAttribute('handle');
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    try {
      const response = await fetch(`${root}products/${handle}?section_id=card-siblings`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const entries = JSON.parse(doc.querySelector('[data-card-siblings]').textContent);

      dataCache.set(card, entries);

      const row = card.querySelector('[data-colour-row]');
      if (row) {
        if (entries.length > 1) {
          const active = parseInt(card.dataset.qsColour || '0', 10);
          let html = entries.map((entry, index) => swatchButtonHtml(entry, index, active)).join('');
          // Mobile shows at most 5 dots (CSS); the "+" reveals the rest
          if (entries.length > 5 && !row.classList.contains('is-expanded')) {
            html += '<button type="button" class="product-card__colour-more" data-colour-more aria-label="Show all colors">+</button>';
          }
          row.innerHTML = html;
        } else {
          // The links resolved to nothing new (e.g. drafts or duplicates)
          row.remove();
        }
        row.removeAttribute('data-siblings-pending');
      }

      card.dataset.qsSiblings = 'loaded';
    } catch (error) {
      // Allow a retry on next hover / intersection
      delete card.dataset.qsSiblings;
    }
  }

  const siblingObserver =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (records) => {
            records.forEach((record) => {
              if (!record.isIntersecting) return;
              siblingObserver.unobserve(record.target);
              loadSiblings(record.target);
            });
          },
          { rootMargin: '300px' }
        )
      : null;

  function watchPendingCards() {
    document.querySelectorAll('[data-siblings-pending]').forEach((row) => {
      const card = row.closest('product-card');
      if (!card || card.dataset.qsSiblings || card.dataset.qsObserved) return;
      card.dataset.qsObserved = 'true';
      if (siblingObserver) siblingObserver.observe(card);
      else loadSiblings(card);
    });
  }

  // Shimmer placeholder: mark the card media as loaded once its primary image
  // is ready, so the CSS shimmer disappears (also on error, to avoid an
  // endless shimmer on broken images)
  function initImageShimmer() {
    document.querySelectorAll('.product-card__media:not(.is-loaded)').forEach((media) => {
      const img = media.querySelector('img.product-card__image--primary');
      if (!img) {
        // No image (placeholder SVG): nothing to wait for
        media.classList.add('is-loaded');
        return;
      }
      if (img.complete && img.naturalWidth > 0) {
        media.classList.add('is-loaded');
        return;
      }
      if (media.dataset.shimmerBound) return;
      media.dataset.shimmerBound = 'true';
      const done = () => media.classList.add('is-loaded');
      img.addEventListener('load', done, { once: true });
      img.addEventListener('error', done, { once: true });
    });
  }

  watchPendingCards();
  initImageShimmer();

  // Re-scan when the grid is re-rendered (filters, sorting, pagination)
  let rescanQueued = false;
  new MutationObserver(() => {
    if (rescanQueued) return;
    rescanQueued = true;
    requestAnimationFrame(() => {
      rescanQueued = false;
      watchPendingCards();
      initImageShimmer();
    });
  }).observe(document.documentElement, { childList: true, subtree: true });

  // Hover fallback: load immediately if the observer hasn't fired yet
  document.addEventListener('pointerover', (event) => {
    const card = event.target.closest && event.target.closest('product-card');
    if (card && card.querySelector('[data-siblings-pending]')) loadSiblings(card);
  });

  document.addEventListener('pointerout', (event) => {
    if (event.pointerType === 'touch' || !isFinePointer()) return;
    const figure = event.target.closest && event.target.closest('.product-card__figure');
    if (!figure) return;
    const next = event.relatedTarget;
    if (next && figure.contains(next)) return;
    const card = figure.closest('product-card');
    if (card) endSliding(card);
  });

  // Mobile / coarse pointer: swipe the card image instead of using arrows.
  let cardSwipe = null;

  document.addEventListener(
    'touchstart',
    (event) => {
      if (event.touches.length !== 1) return;
      const target = event.target;
      if (target.closest('button, a.product-card__colour-swatch, .product-card__size-row, .product-card__colour-row')) {
        return;
      }
      const figure = target.closest('.product-card__figure');
      if (!figure) return;
      const card = figure.closest('product-card');
      if (!card) return;
      cardSwipe = {
        card,
        x: event.touches[0].clientX,
        y: event.touches[0].clientY,
        moved: false,
      };
    },
    { passive: true }
  );

  document.addEventListener(
    'touchmove',
    (event) => {
      if (!cardSwipe || event.touches.length !== 1) return;
      const dx = event.touches[0].clientX - cardSwipe.x;
      const dy = event.touches[0].clientY - cardSwipe.y;
      if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) cardSwipe.moved = true;
    },
    { passive: true }
  );

  document.addEventListener(
    'touchend',
    (event) => {
      if (!cardSwipe) return;
      const { card, x, y, moved } = cardSwipe;
      cardSwipe = null;
      const touch = event.changedTouches[0];
      if (!touch || !moved) return;
      const dx = touch.clientX - x;
      const dy = touch.clientY - y;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
      onNav(card, dx < 0 ? 1 : -1);
      card.dataset.cardSwiped = '1';
      window.setTimeout(() => {
        delete card.dataset.cardSwiped;
      }, 400);
    },
    { passive: true }
  );

  /**
   * Load More pagination on collection pages: fetch the next page, append its
   * product cards to the current grid, and replace the load-more block with
   * the fetched one (updated count + next link). Without JS the button is a
   * plain link to the next page.
   */
  async function onLoadMore(button) {
    if (button.classList.contains('is-loading')) return;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(button.href);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');

      const list = document.querySelector('.collection__results product-list');
      const newList = doc.querySelector('.collection__results product-list');
      if (!list || !newList) {
        // Unexpected markup: fall back to normal navigation
        window.location.href = button.href;
        return;
      }

      Array.from(newList.children).forEach((child) => {
        const node = document.importNode(child, true);
        // The theme's reveal-on-scroll animation only runs when the grid first
        // enters the viewport; appended cards would stay at opacity 0 forever
        node.removeAttribute('reveal-on-scroll');
        node.querySelectorAll('[reveal-on-scroll]').forEach((el) => el.removeAttribute('reveal-on-scroll'));
        list.appendChild(node);
      });

      const currentBlock = button.closest('[data-load-more]');
      const newBlock = doc.querySelector('[data-load-more]');
      if (newBlock) currentBlock.replaceWith(document.importNode(newBlock, true));
      else currentBlock.remove();
    } catch (error) {
      button.classList.remove('is-loading');
      button.setAttribute('aria-busy', 'false');
    }
  }

  async function swapQuickBuyProduct(modal, url) {
    modal.setAttribute('product-url', url);
    modal.setAttribute('aria-busy', 'true');

    const loader = document.createElement('div');
    loader.className = 'quick-buy-loading';
    loader.innerHTML = '<span class="quick-buy-loading__spinner" role="status" aria-label="Loading"></span>';
    modal.replaceChildren(loader);

    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
    try {
      const response = await fetch(url);
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const content = doc.getElementById('quick-buy-content');
      if (content) {
        modal.replaceChildren(content.content.cloneNode(true).firstElementChild);
      }
    } finally {
      modal.setAttribute('aria-busy', 'false');
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }

  // Colour group filter: the visible group checkbox drives the hidden native
  // colour checkboxes inside the same wrapper. Capture phase so children are
  // synced before facets-form serializes the form on the same change event.
  document.addEventListener(
    'change',
    (event) => {
      const toggle = event.target.closest?.('[data-group-toggle]');
      if (!toggle) return;
      const wrapper = toggle.closest('[data-colour-group]');
      if (!wrapper) return;
      wrapper.querySelectorAll('[data-group-child]').forEach((child) => {
        child.checked = toggle.checked;
      });
    },
    true
  );

  // Autoplay blocked (iOS Low Power Mode, data saver, ...): the theme marks the
  // video-media as [suspended] and CSS shows the poster/placeholder instead of
  // the native player. Any tap counts as a user gesture, so retry playback on
  // every interaction until the browser allows it.
  function resumeSuspendedVideos() {
    document.querySelectorAll('video-media[suspended]').forEach((media) => {
      if (typeof media.play === 'function') media.play();
    });
  }
  document.addEventListener('touchend', resumeSuspendedVideos, { passive: true });
  document.addEventListener('click', resumeSuspendedVideos);

  document.addEventListener(
    'click',
    (event) => {
      const media = event.target.closest('a.product-card__media');
      if (!media) return;
      const card = media.closest('product-card');
      if (card && card.dataset.cardSwiped) {
        event.preventDefault();
        event.stopPropagation();
        delete card.dataset.cardSwiped;
      }
    },
    true
  );

  document.addEventListener('click', (event) => {
    const loadMoreBtn = event.target.closest('[data-load-more-btn]');
    if (loadMoreBtn) {
      event.preventDefault();
      onLoadMore(loadMoreBtn);
      return;
    }

    // Colour swatch inside the quick buy drawer: swap the drawer content
    // to the linked colour product instead of navigating away
    const modalSwatch = event.target.closest('quick-buy-modal a.product-colour-swatch');
    if (modalSwatch) {
      event.preventDefault();
      event.stopPropagation();
      swapQuickBuyProduct(modalSwatch.closest('quick-buy-modal'), modalSwatch.getAttribute('href'));
      return;
    }

    const nav = event.target.closest('[data-card-nav]');
    if (nav) {
      const card = nav.closest('product-card');
      if (card) {
        event.preventDefault();
        event.stopPropagation();
        onNav(card, parseInt(nav.dataset.cardNav, 10));
      }
      return;
    }

    const moreBtn = event.target.closest('[data-colour-more]');
    if (moreBtn) {
      const row = moreBtn.closest('[data-colour-row]');
      if (row) {
        row.classList.add('is-expanded');
        moreBtn.remove();
      }
      return;
    }

    const swatch = event.target.closest('.product-card__colour-swatch');
    if (swatch) {
      const card = swatch.closest('product-card');
      if (card) onSwatch(card, swatch);
    }
  });
})();
